/** Import, export, backup and the destructive resets. */
import { useRef } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { readClientCSV, exportClientsCSV, exportBackup } from '../../lib/csv.js';
import { buildClientSeed } from '../../constants/seed.js';
import { DEFAULTS } from '../../constants/defaults.js';
import { Section, Row } from './SettingsBits.jsx';

export default function DataPane() {
  const {
    S, rows, crm, derive, visible, importClients, replaceClients, replaceCrm,
    patchSettings, toast
  } = useApp();

  const csvInput = useRef(null);
  const jsonInput = useRef(null);

  const onCSV = file => {
    const reader = new FileReader();
    reader.onload = () => {
      const { records, error } = readClientCSV(reader.result);
      if (error) { toast(error); return; }
      importClients(records);
    };
    reader.onerror = () => toast('That file could not be read');
    reader.readAsText(file);
  };

  const onBackup = file => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (!data || !Array.isArray(data.clients)) throw new Error('bad shape');
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Replace the current ledger with this backup? ${data.clients.length} clients will be restored.`)) return;
        replaceClients(data.clients);
        if (data.crm) replaceCrm(data.crm);
        if (data.settings) patchSettings({ ...DEFAULTS, ...data.settings });
        toast('Backup restored');
      } catch {
        toast('That backup could not be read');
      }
    };
    reader.onerror = () => toast('That file could not be read');
    reader.readAsText(file);
  };

  const addSamples = () => {
    const sample = buildClientSeed(S);
    const known = new Set(rows.map(r => String(r.email || '').toLowerCase()));
    const fresh = sample.filter(x => !known.has(String(x.email).toLowerCase()));
    if (!fresh.length) { toast('The samples are already here'); return; }
    importClients(fresh.map(({ id: _id, ...rest }) => rest));
  };

  const wipe = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete all ${rows.length} clients? This cannot be undone.`)) return;
    replaceClients([]);
    toast('All clients deleted');
  };

  const reseed = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Replace the ledger with the sample clients?')) return;
    replaceClients(buildClientSeed(S));
    toast('Sample data restored');
  };

  return (
    <>
      <input
        type="file"
        accept=".csv,text/csv"
        hidden
        ref={csvInput}
        onChange={e => { const f = e.target.files[0]; if (f) onCSV(f); e.target.value = ''; }}
      />
      <input
        type="file"
        accept=".json,application/json"
        hidden
        ref={jsonInput}
        onChange={e => { const f = e.target.files[0]; if (f) onBackup(f); e.target.value = ''; }}
      />

      <Section
        title="Import"
        hint="CSV headers are matched automatically. Rows with a matching email update the existing client instead of duplicating it."
      >
        <Row>
          <button type="button" className="btn" onClick={() => csvInput.current?.click()}>
            <Icon name="up" />Import CSV
          </button>
          <button type="button" className="btn" onClick={() => jsonInput.current?.click()}>
            <Icon name="up" />Restore backup
          </button>
          <button type="button" className="btn" onClick={addSamples}>
            <Icon name="zap" />Add sample clients
          </button>
        </Row>
      </Section>

      <Section
        title="Export"
        hint="CSV follows your current filters. The backup holds every client, the pipeline and all settings."
      >
        <Row>
          <button
            type="button"
            className="btn"
            onClick={() => {
              const n = exportClientsCSV(visible('clients'), r => derive(r).type, S.dateFmt);
              toast(`Exported ${n} rows`);
            }}
          >
            <Icon name="down" />Export CSV
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              exportBackup({ settings: S, clients: rows, crm }, S.dateFmt);
              toast('Backup downloaded');
            }}
          >
            <Icon name="down" />Download backup
          </button>
        </Row>
      </Section>

      <Section danger title="Danger zone" hint="These cannot be undone. Download a backup first.">
        <Row>
          <button type="button" className="btn danger" onClick={wipe}>
            <Icon name="trash" />Delete all clients
          </button>
          <button type="button" className="btn danger" onClick={reseed}>
            <Icon name="refresh" />Replace with sample data
          </button>
        </Row>
      </Section>
    </>
  );
}
